import React from 'react';
import ReactDOM from 'react-dom';
import { useData, useField, toReportDateTime, noteTimeRange, reportWallClock, reportTimeOfDay, fromWallClock } from '../EpcrDataContext';
import { Icons } from '../EpcrIcons';
import { ViewLayout, PickerModal, Toast } from '../EpcrFields';

const URLS = require('../../../../urls.js');

// "Pull from Monitor" — plausible cardiac-monitor vitals, timed against the
// call's attached voice notes rather than the current wall-clock time. Both
// the LLM path and the local fallback below produce the same plain shape —
// { minuteOffset, sbp, dbp, hr, spo2, rr, twelveLead, etco2 }[] — so one
// function (buildVitalGroupFromValues) turns either into the actual NEMSIS
// eVitals.VitalGroup entry; the LLM/fallback choice only ever affects where
// the numbers came from, never how they're assembled into the report.
const CARDIAC_KEYWORDS = /cardiac|arrest|defib|shock(?:ed|able)?|v-?fib|v-?tach|asystole|\bpea\b|rosc|resuscitat/i;
const AIRWAY_KEYWORDS = /etco2|capnograph|end.tidal|intubat|advanced airway|supraglottic|king airway|i-?gel|combitube/i;

function randInt(min, max) { return min + Math.round(Math.random() * (max - min)); }

// Used only if the LLM call itself fails (network error, bad JSON, etc.) —
// same keyword-based heuristic the app used before the LLM-assisted path
// existed, kept as a safety net so the button never comes up empty-handed.
function localFallbackVitals(attachedNotes, range) {
  const { earliest, latest } = range;
  const spanMin = Math.max(1, Math.round((latest.getTime() - earliest.getTime()) / 60000));
  const count = Math.max(2, Math.min(6, Math.round(spanMin / 5) + 1));
  const stepMin = count > 1 ? spanMin / (count - 1) : 0;

  const noteText = (attachedNotes || []).map(n => n.text || '').join(' ');
  const includeCardiac = CARDIAC_KEYWORDS.test(noteText);
  const airwayNote = (attachedNotes || []).find(n => AIRWAY_KEYWORDS.test(n.text || ''));
  const airwayOffsetMin = airwayNote
    ? Math.max(0, Math.round((new Date(airwayNote.timestamp).getTime() - earliest.getTime()) / 60000))
    : null;

  return Array.from({ length: count }, (_, i) => {
    const minuteOffset = Math.round(i * stepMin);
    return {
      minuteOffset,
      sbp: randInt(112, 138),
      dbp: randInt(64, 84),
      hr: randInt(78, 112),
      spo2: Math.min(100, randInt(95, 99)),
      rr: randInt(14, 20),
      twelveLead: includeCardiac && i === 0,
      etco2: (airwayOffsetMin != null && minuteOffset >= airwayOffsetMin) ? randInt(35, 45) : null,
    };
  });
}

// Asks the backend for a content-aware set of monitor readings (see
// epcr_vitals_system.txt) — already validated/clamped server-side. Returns
// null on any failure so the caller can fall back to localFallbackVitals.
async function fetchLlmVitalsEstimate(attachedNotes) {
  try {
    const resp = await fetch(URLS.GENERATE_EPCR_DEMO_DATA, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind: 'vitals',
        notes: attachedNotes.map(n => ({ id: n.uid, timestamp: n.timestamp, text: n.text })),
      }),
    });
    const result = await resp.json();
    const sets = result?.vitals?.sets;
    return Array.isArray(sets) && sets.length > 0 ? sets : null;
  } catch (e) {
    console.error('[Pull from Monitor] LLM vitals estimate failed, using local fallback:', e);
    return null;
  }
}

// { minuteOffset, sbp, dbp, hr, spo2, rr, twelveLead, etco2 } + an anchor
// Date -> one eVitals.VitalGroup entry, in the exact shape the sample report
// already uses (BloodPressureGroup/HeartRateGroup/CardiacRhythmGroup as
// nested objects, not arrays — this group is not itself repeating).
function buildVitalGroupFromValues(ts, v) {
  const map = Math.round((v.sbp + 2 * v.dbp) / 3);
  const set = {
    "eVitals.01": toReportDateTime(ts),
    "eVitals.02": "9923001", // Obtained Prior to this Unit's EMS Care — No
    "eVitals.BloodPressureGroup": {
      "eVitals.06": String(v.sbp),
      "eVitals.07": String(v.dbp),
      "eVitals.08": "3308005", // Method of BP Measurement — Cuff-Automated
      "eVitals.09": String(map),
    },
    "eVitals.HeartRateGroup": {
      "eVitals.10": String(v.hr),
      "eVitals.11": "3311007", // Method of HR Measurement — Electronic Monitor (Pulse Ox)
    },
    "eVitals.12": String(v.spo2),
    "eVitals.13": "3313003", // Pulse Rhythm — Regular
    "eVitals.14": String(v.rr),
    "eVitals.15": "3315007", // Respiratory Effort — Normal
    "eVitals.26": "3326001", // Level of Responsiveness (AVPU) — Alert
  };
  if (v.twelveLead) {
    set["eVitals.CardiacRhythmGroup"] = {
      "eVitals.03": v.hr > 100 ? "9901049" : "9901047", // Sinus Tachycardia / Sinus Rhythm
      "eVitals.04": "3304007", // ECG Type — 12 Lead-Left Sided (Normal)
      "eVitals.05": "3305003", // Method of ECG Interpretation — Manual
    };
  }
  if (v.etco2 != null) {
    set["eVitals.16"] = String(v.etco2);
  }
  return set;
}

// ── "Add Vitals" — modeled on ESO's vitals entry screen ──────────────────
// A header strip holds the set-level fields (PTA, time/date, AVPU, side,
// position, UTO, OK); a 2×4 tile rail picks one category; that category's
// numbers are plain underlined inputs typed with an on-screen keypad, and
// its enumerated fields show a few common choices plus an "Other" list.
// data.options()/data.spec() stay the source of truth for NEMSIS codes.

const VITALS_CATEGORIES = [
  { key: 'bp',    label: 'Blood Pressure', icon: Icons.heart,       elems: ['eVitals.06', 'eVitals.07', 'eVitals.08'] },
  { key: 'pulse', label: 'Pulse',          icon: Icons.pulse,       elems: ['eVitals.10', 'eVitals.11', 'eVitals.13'] },
  { key: 'resp',  label: 'Respiration',    icon: Icons.lungs,       elems: ['eVitals.14', 'eVitals.15'] },
  { key: 'spo2',  label: 'SpO₂/EtCO₂/CO',  icon: Icons.droplet,     elems: ['eVitals.12', 'eVitals.16', 'eVitals.17'] },
  { key: 'temp',  label: 'Temp/Glucose',   icon: Icons.thermometer, elems: ['eVitals.24', 'eVitals.25', 'eVitals.18'] },
  { key: 'score', label: 'Scoring',        icon: Icons.brain,       elems: ['eVitals.19', 'eVitals.20', 'eVitals.21', 'eVitals.22', 'eVitals.30', 'eVitals.29', 'eVitals.34', 'eVitals.32', 'eVitals.31'] },
  { key: 'ecg',   label: 'ECG',            icon: Icons.monitor,     elems: ['eVitals.03', 'eVitals.04', 'eVitals.05'] },
  { key: 'pain',  label: 'Pain Scales',    icon: Icons.gauge,       elems: ['eVitals.27', 'eVitals.28'] },
];

// Set-level elements edited in the header strip rather than in a category.
const HEADER_ELEMS = ['eVitals.02', 'eVitals.26'];

// Abnormal-value flags, shared by the entry panel and the table. Each takes
// (number, raw value); the number is NaN for non-numeric values such as a
// palpated "P" diastolic, so every comparison is false for those.
const FLAGS = {
  sbp: n => n > 180 || n < 90,
  dbp: n => n > 110 || n < 60,
  hr: n => n > 120 || n < 50,
  rr: n => n > 24 || n < 8,
  spo2: n => n < 94,
  bgl: (n, raw) => raw === 'High' || raw === 'Low' || n < 70 || n > 250,
  tempF: n => n > 100.4 || n < 95.9,
  tempC: n => n > 38 || n < 35.5,
};

// Numeric inputs per category, in display order. The first one takes the
// keypad's focus when its category opens.
const NUMERIC_FIELDS = {
  bp:    [{ elem: 'eVitals.06', label: 'Systolic', unit: 'mmHg', flag: FLAGS.sbp },
          { elem: 'eVitals.07', label: 'Diastolic', unit: 'mmHg', flag: FLAGS.dbp }],
  pulse: [{ elem: 'eVitals.10', label: 'Heart Rate', unit: 'bpm', flag: FLAGS.hr }],
  resp:  [{ elem: 'eVitals.14', label: 'Respiratory Rate', unit: '/min', flag: FLAGS.rr }],
  spo2:  [{ elem: 'eVitals.12', label: 'SpO₂', unit: '%', flag: FLAGS.spo2 },
          { elem: 'eVitals.16', label: 'EtCO₂', unit: 'mmHg' },
          { elem: 'eVitals.17', label: 'CO', unit: 'ppm' }],
  // Temperature's unit and flag follow the °F/°C toggle (see AddVitalsModal).
  temp:  [{ elem: 'eVitals.24', label: 'Temperature' },
          { elem: 'eVitals.18', label: 'Blood Glucose', unit: 'mg/dL', flag: FLAGS.bgl }],
};

// Scoring's keypad fields, per sub-section. Kept out of NUMERIC_FIELDS so
// the UTO toggle doesn't stamp a stroke score or APGAR onto every set.
const SCORE_FIELDS = {
  stroke: [{ elem: 'eVitals.34', label: 'Stroke Scale Score' }],
  apgar:  [{ elem: 'eVitals.32', label: 'APGAR', unit: '/ 10' }],
};
const SCORE_SECTIONS = [
  { key: 'gcs', label: 'GCS' },
  { key: 'stroke', label: 'Stroke' },
  { key: 'apgar', label: 'APGAR' },
  { key: 'reperfusion', label: 'Reperfusion' },
];

// NEMSIS stores temperature in °C (eVitals.24, one decimal); crews enter °F.
function cToF(c) { return Math.round((c * 9 / 5 + 32) * 10) / 10; }
function fToC(f) { return Math.round(((f - 32) * 5 / 9) * 10) / 10; }

// The few common choices shown as buttons for each enumerated field; the
// rest of the NEMSIS list sits behind "Other". Fields not listed here are
// short enough to show every option.
const FEATURED = {
  'eVitals.08': [['3308007', 'Manual Cuff'], ['3308009', 'Palpated']],
  'eVitals.11': [['3311011', 'Palpated'], ['3311005', 'Monitor']],
  'eVitals.15': [['3315007', 'Normal'], ['3315003', 'Labored'], ['3315011', 'Shallow']],
  'eVitals.25': [['3325007', 'Oral'], ['3325013', 'Tympanic'], ['3325011', 'Temporal']],
  'eVitals.22': [['3322003', 'No Interventions'], ['3322009', 'Intubated']],
  'eVitals.04': [['3304003', '4 Lead'], ['3304007', '12 Lead']],
  'eVitals.05': [['3305003', 'Manual'], ['3305001', 'Computer']],
  'eVitals.28': [['3328003', 'Numeric'], ['3328007', 'Wong-Baker'], ['3328001', 'FLACC']],
  'eVitals.30': [['3330001', 'CPSS'], ['3330029', 'BEFAST'], ['3330023', 'LAMS'], ['3330009', 'NIHSS']],
  'eVitals.29': [['3329001', 'Negative'], ['3329003', 'Non-Conclusive'], ['3329005', 'Positive']],
  'eVitals.31': [['3331003', 'No Contraindications'], ['3331005', 'Possible'], ['3331001', 'Definite']],
};

const BP_PALPATED = '3308009';

// Shown first in the Cardiac Rhythm list, ahead of the full A-Z list.
const COMMON_RHYTHMS = ['9901047', '9901049', '9901045', '9901007', '9901067', '9901069', '9901071', '9901035', '9901003'];

// "Unable to Complete" pertinent negative — what the header's UTO toggle
// writes into every blank numeric vital that allows it.
const UTO_CODE = '8801023';

// Side and Position have no NEMSIS element; they're kept per set under
// custom/VitalsExtras, keyed by the set's eVitals.01 timestamp.
const SIDE_OPTIONS = ['Left', 'Right'].map(v => ({ value: v, label: v }));
const POSITION_OPTIONS = ['Sitting', 'Standing', 'Supine', 'Prone', 'Left Lateral', 'Right Lateral', "Semi-Fowler's"].map(v => ({ value: v, label: v }));

function isFilled(v) { return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0); }
function isPnCode(v) { return typeof v === 'string' && /^88010\d\d$/.test(v); }
function num(v) { const n = Number(v); return isFilled(v) && !isPnCode(v) && Number.isFinite(n) ? n : null; }
function isMulti(data, elem) { return data.spec(elem)?.maxOccurs === 'M'; }

// Multi-value elements (maxOccurs M: rhythm, ECG method, GCS qualifier) are
// stored as a plain string with one value and an array with several, the
// same shape the rest of the report uses for repeating elements.
function asList(v) { return Array.isArray(v) ? v : isFilled(v) ? [v] : []; }
function toggleValue(cur, v, multi) {
  if (!multi) return cur === v ? '' : v;
  const list = asList(cur);
  const next = list.includes(v) ? list.filter(x => x !== v) : [...list, v];
  return next.length === 0 ? '' : next.length === 1 ? next[0] : next;
}

// An element's value in one vitals set, whether it sits at the top level or
// in one of the set's nested groups (BloodPressureGroup, StrokeScaleGroup…).
function findElem(v, elem) {
  if (!v) return undefined;
  if (elem in v) return v[elem];
  for (const g of Object.values(v)) {
    if (g && typeof g === 'object' && !Array.isArray(g) && elem in g) return g[elem];
  }
  return undefined;
}

function computeMap(sbp, dbp) {
  const s = num(sbp), d = num(dbp);
  return s != null && d != null ? Math.round((s + 2 * d) / 3) : null;
}

function computeGcsTotal(e, v, m) {
  const parts = [num(e), num(v), num(m)];
  return parts.every(x => x != null) ? parts[0] + parts[1] + parts[2] : null;
}

// Revised Trauma Score (eVitals.33, 0-12): coded GCS + SBP + RR, each 0-4.
function computeRts(gcsTotal, sbp, rr) {
  const g = num(gcsTotal), s = num(sbp), r = num(rr);
  if (g == null || s == null || r == null) return null;
  const gc = g >= 13 ? 4 : g >= 9 ? 3 : g >= 6 ? 2 : g >= 4 ? 1 : 0;
  const sc = s > 89 ? 4 : s >= 76 ? 3 : s >= 50 ? 2 : s >= 1 ? 1 : 0;
  const rc = r > 29 ? 3 : r >= 10 ? 4 : r >= 6 ? 2 : r >= 1 ? 1 : 0;
  return gc + sc + rc;
}

// ── Controls ─────────────────────────────────────────────────────────────

// Plain underlined input, as in ESO. The list button at its end offers the
// element's pertinent negatives (Refused, Unable to Complete, Exam Finding
// Not Present); a chosen one shows as its label and is cleared with the
// keypad's C. aside: extra buttons beside the input (Glucose HI/LO, °F/°C).
function VitalInput({ label, elem, value, unit, flag, active, onFocus, onChange, aside }) {
  const data = useData();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const pnList = data.spec(elem)?.pn || [];
  const pn = isFilled(value) && pnList.find(x => x.code === value);
  const flagged = !!(flag && !pn && isFilled(value) && flag(Number(value), value));
  return (
    <div className={"av-field av-input" + (active ? " active" : "") + (flagged ? " flag" : "")}>
      <label className="av-label" htmlFor={`av-${elem}`}>{label}</label>
      <div className="av-input-line">
        <span className="av-input-row">
          <input
            id={`av-${elem}`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={pn ? pn.label : (value ?? '')}
            readOnly={!!pn}
            className={pn ? "pn" : ""}
            onFocus={() => onFocus(elem)}
            onChange={e => onChange(e.target.value)}
          />
          {unit && <span className="av-unit">{unit}</span>}
          {pnList.length > 0 && (
            <button type="button" className="av-pn-btn" title="Refused / unable to obtain"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onFocus(elem); setPickerOpen(true); }}>
              <Icons.menu size={18} />
            </button>
          )}
        </span>
        {aside}
      </div>
      {pickerOpen && (
        <PickerModal
          title={label}
          options={pnList.map(p => ({ value: p.code, label: p.label }))}
          value={value}
          onClose={() => setPickerOpen(false)}
          onSelect={v => { onChange(value === v ? '' : v); setPickerOpen(false); }}
        />
      )}
    </div>
  );
}

// Small toggle buttons beside an input — Glucose HI/LO and the °F/°C unit.
function QuickButtons({ options, value, onChange }) {
  return (
    <div className="av-quick">
      {options.map(([v, l]) => (
        <button type="button" key={v} className={"av-choice" + (value === v ? " active" : "")}
          onMouseDown={e => e.preventDefault()}
          onClick={() => onChange(v)}>
          {l}
        </button>
      ))}
    </div>
  );
}

// Read-only computed value with the dashed underline ESO uses.
function ComputedField({ label, value, unit }) {
  return (
    <div className="av-field av-computed">
      <span className="av-label">{label}</span>
      <span className="av-computed-value">{value ?? ''}{value != null && unit && <small> {unit}</small>}</span>
    </div>
  );
}

const KEYPAD_KEYS = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', 'C'];

// On-screen keypad that types into whichever numeric field is active.
// onMouseDown keeps focus (and the caret) in that field.
function Keypad({ onKey }) {
  return (
    <div className="av-keypad">
      {KEYPAD_KEYS.map(k => (
        <button
          type="button"
          key={k}
          className={"av-key" + (k === 'C' ? " clear" : "")}
          onMouseDown={e => e.preventDefault()}
          onClick={() => onKey(k)}
          aria-label={k === 'C' ? 'Clear' : k}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

// A few common choices as buttons, plus "Other ≡" opening the full NEMSIS
// list (including NV/PN values) in the shared picker. A value picked from
// that list takes the Other button's place so it stays visible.
function ChoiceRow({ label, elem, value, onChange, featured }) {
  const data = useData();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const multi = isMulti(data, elem);
  const selected = asList(value);
  const shown = featured || (data.options(elem) || []).map(o => [o.value, o.label]);
  const others = featured ? selected.filter(x => !shown.some(([v]) => v === x)) : [];
  const otherLabel = others.length ? others.map(x => data.label(elem, x) || x).join(', ') : null;
  return (
    <div className="av-field">
      <span className="av-label">{label}{multi && <small> · select all that apply</small>}</span>
      <div className="av-choices">
        {shown.map(([v, l]) => (
          <button type="button" key={v} className={"av-choice" + (selected.includes(v) ? " active" : "")}
            onClick={() => onChange(toggleValue(value, v, multi))}>
            {l}
          </button>
        ))}
        {featured && (
          <button type="button" className={"av-choice av-other" + (otherLabel ? " active" : "")}
            onClick={() => setPickerOpen(true)}>
            <span>{otherLabel || 'Other'}</span>
            <Icons.menu size={18} />
          </button>
        )}
      </div>
      {pickerOpen && (
        <PickerModal
          title={label}
          elem={elem}
          value={value}
          onClose={() => setPickerOpen(false)}
          multi={multi}
          onSelect={v => {
            onChange(toggleValue(value, v, multi));
            if (!multi) setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

// GCS — every option shown as a button in three columns, with the total
// computed rather than asked for.
function shortenGcsLabel(label) {
  return (label || '').split(';')[0].replace(/\s*\([^)]*\)\s*$/, '').trim();
}
function GcsColumns({ data, draft, setField }) {
  const cols = [
    { elem: 'eVitals.19', title: 'Eye' },
    { elem: 'eVitals.20', title: 'Verbal' },
    { elem: 'eVitals.21', title: 'Motor' },
  ];
  return (
    <div className="av-gcs">
      {cols.map(col => {
        const opts = [...(data.options(col.elem) || [])].sort((a, b) => Number(b.value) - Number(a.value));
        return (
          <div className="av-gcs-col" key={col.elem}>
            <span className="av-label">{col.title}</span>
            {opts.map(o => (
              <button
                type="button"
                key={o.value}
                className={"av-gcs-opt" + (draft[col.elem] === o.value ? " active" : "")}
                onClick={() => setField(col.elem, draft[col.elem] === o.value ? '' : o.value)}
                title={o.label}
              >
                <span className="av-gcs-num">{o.value}</span>
                <span>{shortenGcsLabel(o.label)}</span>
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// Cardiac Rhythm — searchable list with the common rhythms first. More than
// one rhythm can be picked (e.g. Sinus Rhythm + PVCs); picks show as chips.
function RhythmList({ value, onChange, options }) {
  const selected = asList(value);
  const [q, setQ] = React.useState('');
  const all = options || [];
  const common = COMMON_RHYTHMS.map(c => all.find(o => o.value === c)).filter(Boolean);
  const rest = all.filter(o => !COMMON_RHYTHMS.includes(o.value));
  const fq = q.trim().toLowerCase();
  const match = o => !fq || o.label.toLowerCase().includes(fq);
  const groups = fq ? [['', all.filter(match)]] : [['Common', common], ['All rhythms', rest]];
  return (
    <div className="av-field">
      <span className="av-label">Cardiac Rhythm<small> · select all that apply</small></span>
      {selected.length > 0 && (
        <div className="av-chips">
          {selected.map(v => (
            <button type="button" key={v} className="av-chip" onClick={() => onChange(toggleValue(value, v, true))}>
              {(all.find(o => o.value === v) || {}).label || v}<Icons.close size={14} />
            </button>
          ))}
        </div>
      )}
      <input className="av-search" placeholder={`Search ${all.length} rhythms…`} value={q} onChange={e => setQ(e.target.value)} />
      <div className="av-rhythm-list">
        {groups.map(([title, list]) => (
          <React.Fragment key={title || 'results'}>
            {title && <div className="av-rhythm-group">{title}</div>}
            {list.map(o => (
              <button
                type="button"
                key={o.value}
                className={"av-rhythm" + (selected.includes(o.value) ? " active" : "")}
                onClick={() => onChange(toggleValue(value, o.value, true))}
              >
                {o.label}
              </button>
            ))}
          </React.Fragment>
        ))}
        {groups.every(([, list]) => list.length === 0) && <div className="av-rhythm-empty">No matches</div>}
      </div>
    </div>
  );
}

// Pain score — 0-10 buttons. Nothing is selected until a number is tapped,
// so 0 is recordable like any other score.
function PainButtons({ value, onChange }) {
  return (
    <div className="av-field">
      <span className="av-label">Pain Score</span>
      <div className="av-pain">
        {Array.from({ length: 11 }, (_, i) => String(i)).map(n => (
          <button
            type="button"
            key={n}
            className={"av-pain-btn" + (value === n ? " active" : "")}
            style={{ '--pain-color': `hsl(${120 - Number(n) * 12}, 65%, 42%)` }}
            onClick={() => onChange(value === n ? '' : n)}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="av-pain-ends"><span>No pain</span><span>Worst pain</span></div>
    </div>
  );
}

function HeadSelect({ label, value, options, onChange }) {
  return (
    <label className="av-head-field av-select">
      <span className="av-label">{label}</span>
      <select value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="" />
        {(options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function toInputDateTimeValue(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Walks data.path(elem) to find which nested group (if any) an element
// belongs to inside one eVitals.VitalGroup entry — e.g. eVitals.27 lives
// under eVitals.PainScaleGroup. Derived from the same canonical path lookup
// every other field in the app uses, rather than hand-maintaining a second
// copy of the group structure that could drift out of sync with it.
function vitalSubgroupKey(data, elem) {
  const path = data.path(elem);
  if (!path) return null;
  const segs = path.split('/');
  const idx = segs.indexOf('eVitals.VitalGroup');
  if (idx === -1) return null;
  const rest = segs.slice(idx + 1);
  return rest.length > 1 ? rest[0] : null;
}

// Elements the modal edits. Everything else in a vitals group (values
// imported from a monitor, elements the panel has no field for) is carried
// over untouched when a set is edited.
const MODAL_ELEMS = [...HEADER_ELEMS, ...VITALS_CATEGORIES.flatMap(c => c.elems)];

// base: the existing eVitals.VitalGroup entry when editing, or null for a
// new set. timestamp: the report dateTime string for eVitals.01.
function buildVitalGroupFromDraft(data, timestamp, draft, base) {
  const group = base ? JSON.parse(JSON.stringify(base)) : {};
  group["eVitals.01"] = timestamp;
  const isObj = v => v && typeof v === 'object' && !Array.isArray(v);
  for (const elem of MODAL_ELEMS) {
    const value = draft[elem];
    const subKey = vitalSubgroupKey(data, elem);
    if (isFilled(value)) {
      if (subKey) group[subKey] = { ...(isObj(group[subKey]) ? group[subKey] : {}), [elem]: value };
      else group[elem] = value;
    } else if (subKey) {
      if (isObj(group[subKey])) delete group[subKey][elem];
    } else {
      delete group[elem];
    }
  }
  // eVitals.02 is Required — a set is "not prior to EMS care" unless PTA is on.
  if (!isFilled(group["eVitals.02"])) group["eVitals.02"] = "9923001";
  // Auto-compute MAP / GCS Total / RTS rather than asking the user for
  // values that are directly derivable from what they already entered.
  const bp = group["eVitals.BloodPressureGroup"];
  if (isObj(bp)) {
    const map = computeMap(bp["eVitals.06"], bp["eVitals.07"]);
    if (map != null) bp["eVitals.09"] = String(map);
    else delete bp["eVitals.09"];
  }
  const gcs = group["eVitals.GlasgowScoreGroup"];
  if (isObj(gcs)) {
    const parts = ["eVitals.19", "eVitals.20", "eVitals.21"];
    const total = computeGcsTotal(...parts.map(e => gcs[e]));
    if (total != null) {
      gcs["eVitals.23"] = String(total);
    } else if (parts.some(e => isFilled(gcs[e]))) {
      // A partial GCS can't support a total; with no components at all, a
      // total recorded on its own (allowed by NEMSIS) is left as it was.
      delete gcs["eVitals.23"];
    }
  }
  // RTS is only written when all three inputs exist, so an imported score
  // survives an edit that doesn't touch them.
  const rts = computeRts(gcs?.["eVitals.23"], bp?.["eVitals.06"], group["eVitals.14"]);
  if (rts != null) group["eVitals.33"] = String(rts);
  return group;
}

// Inverse of buildVitalGroupFromDraft — reads an existing eVitals.VitalGroup
// entry back into the modal's flat { elem: value } draft shape, using the
// same MODAL_ELEMS list + vitalSubgroupKey lookup so the two directions can
// never drift out of sync with each other.
function draftFromVitalGroup(data, group) {
  const draft = {};
  for (const elem of MODAL_ELEMS) {
    const subKey = vitalSubgroupKey(data, elem);
    const val = subKey ? group[subKey]?.[elem] : group[elem];
    if (val !== undefined && val !== null && val !== '') draft[elem] = val;
  }
  return draft;
}

function AddVitalsModal({ editGroup, editExtras, onClose, onSave, onDelete }) {
  const data = useData();
  const isEditing = !!editGroup;
  const [category, setCategory] = React.useState('bp');
  const [when, setWhen] = React.useState(() => {
    const wall = reportWallClock(editGroup?.["eVitals.01"]) || toInputDateTimeValue(new Date());
    return { date: wall.slice(0, 10), time: wall.slice(11, 19) };
  });
  const [draft, setDraft] = React.useState(() => (editGroup ? draftFromVitalGroup(data, editGroup) : {}));
  const [extras, setExtras] = React.useState(() => ({ ...(editExtras || {}) }));
  const [activeField, setActiveField] = React.useState(NUMERIC_FIELDS.bp[0].elem);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [scoreSection, setScoreSection] = React.useState('gcs');
  // Temperature is typed in tempUnit and stored in °C. tempEntry keeps the
  // text as typed so a half-typed "98." isn't rewritten by the conversion.
  const [tempUnit, setTempUnit] = React.useState('F');
  const [tempEntry, setTempEntry] = React.useState(() => {
    const c = num(draft['eVitals.24']);
    return c != null ? String(cToF(c)) : '';
  });

  const setField = (elem, value) => setDraft(d => ({ ...d, [elem]: value }));

  // Elements the UTO toggle fills: every numeric vital that allows the
  // "Unable to Complete" pertinent negative.
  const utoElems = React.useMemo(
    () => Object.values(NUMERIC_FIELDS).flat().map(f => f.elem)
      .filter(e => (data.spec(e)?.pn || []).some(p => p.code === UTO_CODE)),
    [data]
  );
  const uto = utoElems.some(e => draft[e] === UTO_CODE);
  const toggleUto = () => setDraft(d => {
    const next = { ...d };
    for (const e of utoElems) {
      if (uto && next[e] === UTO_CODE) next[e] = '';
      else if (!uto && !isFilled(next[e])) next[e] = UTO_CODE;
    }
    return next;
  });

  const pta = draft['eVitals.02'] === '9923003';

  const storedTemp = draft['eVitals.24'];
  const tempShown = !isFilled(storedTemp) ? '' : isPnCode(storedTemp) ? storedTemp : tempEntry;
  const setTemp = (text) => {
    setTempEntry(text);
    const n = Number(text);
    const stored = text === '' || isPnCode(text) || !Number.isFinite(n) ? text
      : String(tempUnit === 'F' ? fToC(n) : n);
    setField('eVitals.24', stored);
  };
  const switchTempUnit = (unit) => {
    if (unit === tempUnit) return;
    setTempUnit(unit);
    const c = num(storedTemp);
    if (c != null) setTempEntry(String(unit === 'F' ? cToF(c) : c));
  };

  const scoreFields = category === 'score' ? SCORE_FIELDS[scoreSection] : null;
  const keypadFields = NUMERIC_FIELDS[category] || scoreFields;

  const openCategory = (key) => {
    setCategory(key);
    const fields = NUMERIC_FIELDS[key] || (key === 'score' ? SCORE_FIELDS[scoreSection] : null);
    if (fields) setActiveField(fields[0].elem);
  };

  const openScoreSection = (key) => {
    setScoreSection(key);
    if (SCORE_FIELDS[key]) setActiveField(SCORE_FIELDS[key][0].elem);
  };

  const handleKey = (k) => {
    if (!activeField) return;
    // The header's Time field takes keypad digits too, 24-hour HH:MM:SS
    // with the colons filled in as you type, the way ESO's time entry works.
    if (activeField === 'time') {
      if (k === '.') return;
      setWhen(w => {
        const digits = k === 'C' ? '' : (w.time.replace(/\D/g, '') + k).slice(0, 6);
        return { ...w, time: digits.replace(/(\d{2})(?=\d)/g, '$1:') };
      });
      return;
    }
    const isTemp = activeField === 'eVitals.24';
    const set = isTemp ? setTemp : v => setField(activeField, v);
    const cur = isTemp ? tempShown : draft[activeField];
    if (k === 'C') { set(''); return; }
    // A code or word (UTO, "P", "High") is replaced, not appended to.
    const base = isFilled(cur) && /^[\d.]+$/.test(String(cur)) ? String(cur) : '';
    if (k === '.' && base.includes('.')) return;
    set(base + k);
  };

  // Picking "Palpated" records diastolic as "P", the NEMSIS value for a
  // palpated pressure, unless a diastolic was already entered.
  const setBpMethod = (v) => setDraft(d => {
    const next = { ...d, 'eVitals.08': v };
    if (v === BP_PALPATED && !isFilled(d['eVitals.07'])) next['eVitals.07'] = 'P';
    if (v !== BP_PALPATED && d['eVitals.07'] === 'P') next['eVitals.07'] = '';
    return next;
  });

  const timeValid = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(when.time);

  const handleSubmit = () => {
    const local = `${when.date}T${when.time}`;
    if (!when.date || !timeValid || isNaN(new Date(local).getTime())) {
      setActiveField('time');
      return;
    }
    onSave(buildVitalGroupFromDraft(data, fromWallClock(local, editGroup?.["eVitals.01"]), draft, editGroup), extras);
  };

  const vitalInput = (f, extra = {}) => (
    <VitalInput key={f.elem} {...f} value={draft[f.elem]} active={activeField === f.elem}
      onFocus={setActiveField} onChange={v => setField(f.elem, v)} {...extra} />
  );
  const numericInputs = (key) => (NUMERIC_FIELDS[key] || []).map(f => vitalInput(f));

  const tempInput = vitalInput(NUMERIC_FIELDS.temp[0], {
    value: tempShown,
    unit: `°${tempUnit}`,
    flag: tempUnit === 'F' ? FLAGS.tempF : FLAGS.tempC,
    onChange: setTemp,
    aside: <QuickButtons options={[['F', '°F'], ['C', '°C']]} value={tempUnit} onChange={switchTempUnit} />,
  });
  const glucoseInput = vitalInput(NUMERIC_FIELDS.temp[1], {
    aside: <QuickButtons options={[['High', 'HI'], ['Low', 'LO']]} value={draft['eVitals.18']}
      onChange={v => setField('eVitals.18', draft['eVitals.18'] === v ? '' : v)} />,
  });

  const choice = (elem, label, onChange = v => setField(elem, v)) => (
    <ChoiceRow label={label} elem={elem} value={draft[elem]} onChange={onChange} featured={FEATURED[elem]} />
  );

  const renderCategory = () => {
    const map = computeMap(draft['eVitals.06'], draft['eVitals.07']);
    const sbp = num(draft['eVitals.06']), hr = num(draft['eVitals.10']);
    const gcsTotal = computeGcsTotal(draft['eVitals.19'], draft['eVitals.20'], draft['eVitals.21']);
    switch (category) {
      case 'bp':
        return (
          <>
            {numericInputs('bp')}
            <ComputedField label="Mean Arterial Pressure" value={map} unit="mmHg" />
            {choice('eVitals.08', 'Method', setBpMethod)}
            <ComputedField label="Shock Index" value={sbp && hr != null ? (hr / sbp).toFixed(2) : null} />
          </>
        );
      case 'pulse':
        return <>{numericInputs('pulse')}{choice('eVitals.11', 'Method')}{choice('eVitals.13', 'Rhythm')}</>;
      case 'resp':
        return <>{numericInputs('resp')}{choice('eVitals.15', 'Effort')}</>;
      case 'spo2':
        return numericInputs('spo2');
      case 'temp':
        return <>{tempInput}{choice('eVitals.25', 'Temperature Method')}{glucoseInput}</>;
      case 'score':
        return (
          <>
            <div className="av-subtabs">
              {SCORE_SECTIONS.map(sec => {
                const elems = { gcs: ['eVitals.19', 'eVitals.20', 'eVitals.21', 'eVitals.22'], stroke: ['eVitals.29', 'eVitals.30', 'eVitals.34'], apgar: ['eVitals.32'], reperfusion: ['eVitals.31'] }[sec.key];
                return (
                  <button type="button" key={sec.key} className={"av-subtab" + (scoreSection === sec.key ? " active" : "")}
                    onClick={() => openScoreSection(sec.key)}>
                    {sec.label}{elems.some(e => isFilled(draft[e])) && <span className="av-subtab-dot" />}
                  </button>
                );
              })}
            </div>
            {scoreSection === 'gcs' && (
              <>
                <GcsColumns data={data} draft={draft} setField={setField} />
                <div className="av-row-2">
                  <ComputedField label="GCS Total" value={gcsTotal != null ? `${gcsTotal} / 15` : null} />
                  <ComputedField label="Revised Trauma Score" value={computeRts(gcsTotal, draft['eVitals.06'], draft['eVitals.14']) ?? editGroup?.["eVitals.33"] ?? null} unit="/ 12" />
                </div>
                {choice('eVitals.22', 'GCS Qualifier')}
              </>
            )}
            {scoreSection === 'stroke' && (
              <>
                {choice('eVitals.30', 'Stroke Scale Type')}
                {choice('eVitals.29', 'Result')}
                {SCORE_FIELDS.stroke.map(f => vitalInput(f))}
              </>
            )}
            {scoreSection === 'apgar' && SCORE_FIELDS.apgar.map(f => vitalInput(f))}
            {scoreSection === 'reperfusion' && choice('eVitals.31', 'Reperfusion Checklist')}
          </>
        );
      case 'ecg':
        return (
          <>
            <RhythmList value={draft['eVitals.03']} onChange={v => setField('eVitals.03', v)} options={data.options('eVitals.03')} />
            {choice('eVitals.04', 'ECG Type')}
            {choice('eVitals.05', 'Interpretation')}
          </>
        );
      case 'pain':
        return <><PainButtons value={draft['eVitals.27']} onChange={v => setField('eVitals.27', v)} />{choice('eVitals.28', 'Pain Scale Type')}</>;
      default:
        return null;
    }
  };

  const activeCategory = VITALS_CATEGORIES.find(c => c.key === category);

  return ReactDOM.createPortal(
    <div className="epcr-pm-back" onClick={onClose}>
      {/* epcr-root re-opens the scoped --epcr-* variables locally, since this
          portal renders outside .epcr-root in the DOM. Every av-* rule is
          written as ".epcr-av .av-…" so it outranks .epcr-root's own button
          reset (see EpcrPage.css). */}
      <div className="epcr-av epcr-root" onClick={e => e.stopPropagation()}>
        <div className="av-head">
          <div className="av-head-toggle">
            <button type="button" className={"av-toggle" + (pta ? " on" : "")}
              onClick={() => setField('eVitals.02', pta ? '9923001' : '9923003')}
              title="Prior to arrival — obtained before this unit's care">
              <span className="av-toggle-dot" />PTA
            </button>
          </div>
          <label className={"av-head-field av-time" + (activeField === 'time' ? " active" : "") + (timeValid ? "" : " invalid")}>
            <span className="av-label">Time</span>
            <input type="text" inputMode="numeric" placeholder="HH:MM:SS" value={when.time}
              onFocus={() => setActiveField('time')}
              onChange={e => setWhen(w => ({ ...w, time: e.target.value }))} />
          </label>
          <label className="av-head-field av-date">
            <span className="av-label">Date</span>
            <input type="date" value={when.date} onChange={e => setWhen(w => ({ ...w, date: e.target.value }))} />
          </label>
          <HeadSelect label="AVPU" value={draft['eVitals.26']} options={data.options('eVitals.26')} onChange={v => setField('eVitals.26', v)} />
          <HeadSelect label="Side" value={extras.side} options={SIDE_OPTIONS} onChange={v => setExtras(x => ({ ...x, side: v }))} />
          <HeadSelect label="Position" value={extras.position} options={POSITION_OPTIONS} onChange={v => setExtras(x => ({ ...x, position: v }))} />
          <div className="av-head-toggle">
            <button type="button" className={"av-toggle" + (uto ? " on" : "")} onClick={toggleUto}
              title="Unable to obtain — marks every blank vital as Unable to Complete">
              <span className="av-toggle-dot" />UTO
            </button>
          </div>
          <div className="av-head-actions">
            {isEditing && (
              <button type="button" className={"av-delete" + (confirmDelete ? " confirm" : "")}
                onClick={() => (confirmDelete ? onDelete() : setConfirmDelete(true))}
                onBlur={() => setConfirmDelete(false)}
                aria-label="Delete this vitals set">
                <Icons.trash size={20} />{confirmDelete && <span>Delete?</span>}
              </button>
            )}
            <button type="button" className="av-ok" onClick={handleSubmit}>OK</button>
            <button type="button" className="av-close" onClick={onClose} aria-label="Close"><Icons.close size={22} /></button>
          </div>
        </div>
        <div className="av-body">
          <div className="av-rail">
            {VITALS_CATEGORIES.map(c => (
              <button
                type="button"
                key={c.key}
                className={"av-tile" + (category === c.key ? " active" : "")}
                onClick={() => openCategory(c.key)}
              >
                <span className="av-tile-label">{c.label}</span>
                {c.elems.some(e => isFilled(draft[e])) && <span className="av-tile-dot" />}
                <c.icon size={64} />
              </button>
            ))}
          </div>
          <div className="av-panel">
            <div className="av-fields">
              <h4 className="av-title">{activeCategory.label}</h4>
              {renderCategory()}
            </div>
            {keypadFields && <Keypad onKey={handleKey} />}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Table columns per sub-tab ─────────────────────────────────────────────
// Each column resolves its own raw value from a vitals entry; resolveElem
// (when set) additionally looks that raw code up to a plaintext label via
// data.label(). One shared table renderer (VitalsTable) uses these to build
// both the header row and every body row, so column definition is the only
// thing that differs between the General/Scoring/ECG panels — row markup,
// height, and styling are identical across all three by construction.

const GENERAL_COLUMNS = [
  { label: 'BP', unit: 'mmHg', elem: 'eVitals.06', get: v => {
    const bp = v['eVitals.BloodPressureGroup'] || {};
    if (isPnCode(bp['eVitals.06'])) return bp['eVitals.06'];
    return (bp['eVitals.06'] && bp['eVitals.07']) ? `${bp['eVitals.06']}/${bp['eVitals.07']}` : null;
  } },
  { label: 'MAP', unit: 'mmHg', get: v => v['eVitals.BloodPressureGroup']?.['eVitals.09'] },
  { label: 'HR', unit: 'bpm', elem: 'eVitals.10', get: v => v['eVitals.HeartRateGroup']?.['eVitals.10'], flag: FLAGS.hr },
  { label: 'SpO₂', unit: '%', elem: 'eVitals.12', get: v => v['eVitals.12'], flag: FLAGS.spo2 },
  { label: 'RR', unit: '/min', elem: 'eVitals.14', get: v => v['eVitals.14'], flag: FLAGS.rr },
  { label: 'ETCO₂', unit: 'mmHg', elem: 'eVitals.16', get: v => v['eVitals.16'] },
  { label: 'BGL', unit: 'mg/dL', elem: 'eVitals.18', get: v => v['eVitals.18'], flag: FLAGS.bgl },
  { label: 'Temp', unit: '°F', elem: 'eVitals.24', get: v => {
    const t = findElem(v, 'eVitals.24');
    return num(t) != null ? String(cToF(num(t))) : t;
  }, flag: FLAGS.tempF },
];

const SCORING_COLUMNS = [
  { label: 'GCS-E', unit: '/4', get: v => v['eVitals.GlasgowScoreGroup']?.['eVitals.19'] },
  { label: 'GCS-V', unit: '/5', get: v => v['eVitals.GlasgowScoreGroup']?.['eVitals.20'] },
  { label: 'GCS-M', unit: '/6', get: v => v['eVitals.GlasgowScoreGroup']?.['eVitals.21'] },
  { label: 'GCS Total', unit: '/15', get: v => v['eVitals.GlasgowScoreGroup']?.['eVitals.23'], flag: n => n < 13 },
  { label: 'AVPU', get: v => v['eVitals.26'], resolveElem: 'eVitals.26' },
  // eVitals.33 is Revised Trauma Score, not a stroke scale — this column was
  // mislabeled "Stroke"/"Stroke (CPSS)" before this rewrite.
  { label: 'Trauma Score', get: v => v['eVitals.33'] },
  { label: 'Stroke', elem: 'eVitals.29', get: v => findElem(v, 'eVitals.29'), resolveElem: 'eVitals.29', flag: (n, raw) => raw === '3329005' },
  { label: 'Stroke Score', elem: 'eVitals.34', get: v => findElem(v, 'eVitals.34') },
  { label: 'Pain', unit: '/10', elem: 'eVitals.27', get: v => v['eVitals.PainScaleGroup']?.['eVitals.27'] },
];

const ECG_COLUMNS = [
  { label: 'Rhythm', get: v => v['eVitals.CardiacRhythmGroup']?.['eVitals.03'], resolveElem: 'eVitals.03' },
  { label: 'Type', get: v => v['eVitals.CardiacRhythmGroup']?.['eVitals.04'], resolveElem: 'eVitals.04' },
  { label: 'Method', get: v => v['eVitals.CardiacRhythmGroup']?.['eVitals.05'], resolveElem: 'eVitals.05' },
];

function VitalsTable({ columns, sorted, data, time, onRowClick }) {
  return (
    <div className="vitals-table-wrap">
      <table className="vitals-table">
        <thead>
          <tr>
            <th className="vt-time-col">Time</th>
            {columns.map(c => (
              <th key={c.label}>
                <span className="vt-name">{c.label}</span>
                {c.unit && <span className="vt-unit">{c.unit}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(({ v, origIdx }, i) => (
            <tr key={i} data-group-prefix="eVitals" data-group-index={origIdx}
                className="vt-row" onClick={() => onRowClick(origIdx)}>
              <td className="vt-time-col"><span className="vt-hh">{time(v["eVitals.01"])}</span></td>
              {columns.map(c => {
                const raw = c.get(v);
                const empty = raw == null || raw === '';
                const pn = !empty && c.elem && isPnCode(raw) ? (data.spec(c.elem)?.pn || []).find(x => x.code === raw) : null;
                const display = pn ? (raw === UTO_CODE ? 'UTO' : pn.label)
                  : !empty && c.resolveElem ? asList(raw).map(r => data.label(c.resolveElem, r) || r).join(', ')
                  : raw;
                const flagged = !empty && !pn && c.flag && c.flag(Number(raw), raw);
                return (
                  <td key={c.label} className={flagged ? 'flag' : pn ? 'pn' : ''}>
                    {empty ? <span className="vt-empty">—</span> : display}
                  </td>
                );
              })}
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr><td className="vt-empty-row" colSpan={columns.length + 1}>No vitals recorded yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const SUBTABS = [
  { key: 'general', label: 'General' },
  { key: 'scoring', label: 'Scoring' },
  { key: 'ecg',     label: 'ECG' },
];

export function EpcrViewVitals({ attachedNotes = [] }) {
  const [tab, setTab] = React.useState("general");
  const [monitorError, setMonitorError] = React.useState(false);
  const [monitorBusy, setMonitorBusy] = React.useState(false);
  // null (closed) | 'new' | <origIdx> — the array index of the set being
  // edited, when a table row was clicked to pull it back up for editing.
  const [modalTarget, setModalTarget] = React.useState(null);
  const [toast, setToast] = React.useState(null);
  const data = useData();

  const vitals = useField("eVitals/eVitals.VitalGroup") || [];
  const vitalsExtras = useField("custom/VitalsExtras") || {};
  const sorted = vitals
    .map((v, i) => ({ v, origIdx: i }))
    .sort((a, b) => (a.v["eVitals.01"] || "").localeCompare(b.v["eVitals.01"] || ""));

  const time = (iso) => reportTimeOfDay(iso); // HH:MM:SS as recorded

  const handlePullFromMonitor = async () => {
    if (monitorBusy) return;
    const range = noteTimeRange(attachedNotes);
    if (!range) {
      setMonitorError(true);
      setTimeout(() => setMonitorError(false), 2000);
      return;
    }
    setMonitorError(false);
    setMonitorBusy(true);
    const values = (await fetchLlmVitalsEstimate(attachedNotes)) || localFallbackVitals(attachedNotes, range);
    const newGroups = values.map(v => buildVitalGroupFromValues(new Date(range.earliest.getTime() + v.minuteOffset * 60000), v));
    const current = Array.isArray(vitals) ? vitals : [];
    data.setReportPath("eVitals/eVitals.VitalGroup", [...current, ...newGroups]);
    setMonitorBusy(false);
    setToast(`Pulled ${newGroups.length} vitals set${newGroups.length === 1 ? '' : 's'} from monitor.`);
  };

  // Side/Position live under custom/VitalsExtras keyed by eVitals.01, so a
  // set's entry moves with it when its time is edited.
  const writeExtras = (oldKey, newKey, extras) => {
    const hadOld = !!oldKey && oldKey in vitalsExtras;
    const hasNew = !!newKey && !!extras && !!(extras.side || extras.position);
    if (!hadOld && !hasNew) return;
    const next = { ...vitalsExtras };
    if (hadOld) delete next[oldKey];
    if (hasNew) next[newKey] = extras;
    data.setReportPath("custom/VitalsExtras", next);
  };

  const handleSaveVitals = (group, extras) => {
    const current = Array.isArray(vitals) ? vitals : [];
    const oldKey = modalTarget === 'new' ? null : current[modalTarget]?.["eVitals.01"];
    writeExtras(oldKey, group["eVitals.01"], extras);
    if (modalTarget === 'new') {
      data.setReportPath("eVitals/eVitals.VitalGroup", [...current, group]);
      setToast('Vitals added.');
    } else {
      const next = current.map((g, i) => i === modalTarget ? group : g);
      data.setReportPath("eVitals/eVitals.VitalGroup", next);
      setToast('Vitals updated.');
    }
    setModalTarget(null);
  };

  const handleDeleteVitals = () => {
    const current = Array.isArray(vitals) ? vitals : [];
    writeExtras(current[modalTarget]?.["eVitals.01"], null, null);
    data.setReportPath("eVitals/eVitals.VitalGroup", current.filter((_, i) => i !== modalTarget));
    setModalTarget(null);
    setToast('Vitals deleted.');
  };

  const subbar = (
    <div className="vitals-sub">
      <div className="vitals-sub-spacer"><span className="vitals-total">Total {sorted.length} Vitals</span></div>
      <div className="vitals-sub-tabs">
        {SUBTABS.map(t => (
          <button key={t.key} className={tab === t.key ? "active" : ""} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      {/* <div className="vitals-sub-status">
        <span className="status-pill"><span className="dt" /> {sorted.length} sets recorded</span>
      </div> */}
    </div>
  );

  const addBar = (
    <div className="add-bar">
      <button className="btn-primary" onClick={() => setModalTarget('new')}><Icons.plus size={14} /> Add vitals</button>
      <button
        className={"btn-secondary" + (monitorError ? " btn-error" : "")}
        onClick={handlePullFromMonitor}
        disabled={monitorBusy}
        title={monitorError ? "No timestamped voice notes attached to this call" : undefined}
      >
        <Icons.pulse size={14} /> {monitorBusy ? "Pulling…" : "Pull from monitor"}
      </button>
    </div>
  );

  const tabIndex = SUBTABS.findIndex(t => t.key === tab);
  const handleRowClick = (origIdx) => setModalTarget(origIdx);

  return (
    <>
    <ViewLayout sections={[]} subbar={subbar} addBar={addBar} hideNav>
      <div className="vitals-carousel-viewport">
        <div className="vitals-carousel-track" style={{ transform: `translateX(-${tabIndex * 100}%)` }}>
          <div className="vitals-panel"><VitalsTable columns={GENERAL_COLUMNS} sorted={sorted} data={data} time={time} onRowClick={handleRowClick} /></div>
          <div className="vitals-panel"><VitalsTable columns={SCORING_COLUMNS} sorted={sorted} data={data} time={time} onRowClick={handleRowClick} /></div>
          <div className="vitals-panel"><VitalsTable columns={ECG_COLUMNS} sorted={sorted} data={data} time={time} onRowClick={handleRowClick} /></div>
        </div>
      </div>
    </ViewLayout>
    {modalTarget !== null && (
      <AddVitalsModal
        editGroup={modalTarget === 'new' ? null : vitals[modalTarget]}
        editExtras={modalTarget === 'new' ? null : vitalsExtras[vitals[modalTarget]?.["eVitals.01"]]}
        onClose={() => setModalTarget(null)}
        onSave={handleSaveVitals}
        onDelete={handleDeleteVitals}
      />
    )}
    {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </>
  );
}
